import { getBinaryMarketOptions } from '@/client/pages/home/components/market-card/binary-options';
import { useAppSettingStore } from '@/client/store/app-store';
import { api } from '@/client/utils/api';
import type { Market, MarketOrderInfo } from '@/libs/sdk/api';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

type MarketRef = {
  marketAddress: string;
  marketName: string;
  marketNameI18n?: Market['nameI18n'];
  // Whether this trade's option is the market's positive side (Yes / Up /
  // Higher). Resolved the same way the rest of the app does (getBinaryOptions),
  // so the broadcast colours the outcome consistently with every other surface.
  isPrimaryOutcome: boolean;
};

export type ActivityTrade = MarketOrderInfo & MarketRef;

// Site-wide recent trades. `get-market-trades` with an empty marketAddress
// degrades to a global, id-desc feed (newest first) across every market --
// the backing list for the footer activity broadcast. Kept as an app-local
// hook (not the SSE-gated useMarketTrades, which refuses an empty address).
const useRecentTrades = (pageSize = 20) =>
  useQuery<MarketOrderInfo[]>({
    queryKey: ['RecentTrades', pageSize],
    queryFn: async () => {
      const { data } = await api.market.getMarketTrades({ marketAddress: '', page: 1, pageSize });
      return data?.orders ?? [];
    },
    // Ambient broadcast, not a trading surface -- a slow poll keeps it fresh
    // without hammering the feed.
    refetchInterval: 20_000,
    staleTime: 15_000,
  });

// option-token address -> owning market. The global trades feed carries only
// the option token on each trade, not its market, so we resolve it here from
// the markets list (34 markets fit in one page).
const useOptionMarketMap = () => {
  const lens = useAppSettingStore(s => s.baseToken.address);
  const { data } = useQuery<Market[]>({
    queryKey: ['ActivityMarketMap', lens],
    queryFn: async () => {
      const { data } = await api.market.getMarkets({ page: 1, pageSize: 100, baseTokenAddress: lens });
      return data?.markets ?? [];
    },
    staleTime: 60_000,
  });

  return useMemo(() => {
    const map = new Map<string, MarketRef>();
    for (const m of data ?? []) {
      const { yes } = getBinaryMarketOptions(m.options ?? []);
      const yesAddr = yes?.tokenAddress?.toLowerCase();
      for (const o of m.options ?? []) {
        if (!o.tokenAddress) continue;
        map.set(o.tokenAddress.toLowerCase(), {
          marketAddress: m.marketAddress,
          marketName: m.marketName,
          marketNameI18n: m.nameI18n,
          isPrimaryOutcome: !!yesAddr && o.tokenAddress.toLowerCase() === yesAddr,
        });
      }
    }
    return map;
  }, [data]);
};

// Recent trades enriched with their owning market. Trades whose option cannot
// be resolved to a market are dropped, so every returned row is renderable.
export const useRecentActivity = (pageSize = 20): ActivityTrade[] => {
  const { data: trades } = useRecentTrades(pageSize);
  const marketMap = useOptionMarketMap();

  return useMemo(() => {
    const out: ActivityTrade[] = [];
    for (const trade of trades ?? []) {
      const ref = marketMap.get(trade.option?.address?.toLowerCase() ?? '');
      if (!ref || !trade.userName) continue;
      out.push({ ...trade, ...ref });
    }
    return out;
  }, [trades, marketMap]);
};
